import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, EMPTY, iif, map, Subject, switchMap, takeUntil, timer } from 'rxjs';
import { IPromosEcommerce, IProductos } from '../../../../private/shared/models/productos';
import { MaxDiscountsLimitPage } from '../../../modals/max-discounts-limit/max-discounts-limit.page';
import { IAnalyticsListContext } from '../../../models/Analytics';
import { ModalsService } from '../../../services/modals/modals.service';
import { environment } from './../../../../../environments/environment';

@Component({
  selector: 'getm-body-detail-product',
  templateUrl: './body-detail-product.component.html',
  styleUrls: ['./body-detail-product.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BodyDetailProductComponent implements OnInit, OnDestroy {

  @Input() product?: IProductos;
  @Input() set promo(value: IPromosEcommerce | undefined) {
    if (!value) return;
    this.promoData = value;
    this.quantity = value.PaquetesCanjeados;
    this.claveProducto = value.Productos && value.Productos.length > 0 ? value.Productos[0].clave_producto : 0;
    this.extrasQuantity();
  }
  @Input() analyticsContext?: IAnalyticsListContext;

  @Output() quantityChange = new EventEmitter<IProductos>();
  @Output() quantityPromoChange = new EventEmitter<{ ClavePromo: string, quantity: number }>();
  @Output() quantityPromoExtrasChange = new EventEmitter<{ ClavePromo: string, clave_producto: number, quantity: number }>();

  public isProd = environment.production;
  public promoData: IPromosEcommerce = {} as IPromosEcommerce;
  public quantity: number = 0;
  public quantityExtras$ = new BehaviorSubject<number>(0);
  public claveProducto: number = 0;
  public activeSlide: number = 0;

  private emitirExtras: boolean = false;
  private finishSubscriptions: Subject<boolean> = new Subject();

  constructor(
    private modalsS: ModalsService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.quantityExtras$
      .pipe(
        distinctUntilChanged((prev, curr) => (prev > 0) === (curr > 0)),
        switchMap(quantity =>
          iif(() => quantity > 0,
            timer(3000, 3000).pipe(map((tick) => (tick + 1) % 2)),
            EMPTY
          )
        ),
        takeUntil(this.finishSubscriptions)
      )
      .subscribe(this.callBackStartAutoPlay);
  }

  ngOnDestroy() {
    this.finishSubscriptions.next(true);
    this.finishSubscriptions.complete();
  }

  public onQuantityChange = (quantity: number): void => {
    if (this.product) {
      this.product = { ...this.product, productos: quantity };
      this.quantityChange.emit(this.product);
    }
  }

  public onRemoveProduct = (): void => {
    if (this.product) {
      this.product = { ...this.product, productos: 0 };
      this.quantityChange.emit(this.product);
    }
  }

  public onBuildPromo = async () => {
    if (this.promoData.MaximoPaquetes - this.promoData.PaquetesCanjeados < 0) return;
    this.quantity = 1;
    this.quantityPromoChange.emit({ ClavePromo: this.promoData.ClavePromo, quantity: this.quantity });
    this.extrasQuantity();
    this.cdr.markForCheck();
  }

  public onQuantityChangePromo = async (quantity: number) => {
    const oldQuantity = this.quantity;
    this.quantity = quantity;
    if (this.quantity === this.promoData.MaximoPaquetes + 1 && oldQuantity === this.promoData.MaximoPaquetes) {
      const accept = await this.modalsS.presentModalMaxDiscountsLimit(MaxDiscountsLimitPage);
      if (!accept) {
        this.quantity = this.promoData.MaximoPaquetes;
        this.cdr.markForCheck();
        return;
      }
    }

    this.quantityPromoChange.emit({ ClavePromo: this.promoData.ClavePromo, quantity });
    this.extrasQuantity();
    if (this.emitirExtras) {
      this.quantityPromoExtrasChange.emit({
        ClavePromo: this.promoData.ClavePromo,
        clave_producto: this.claveProducto,
        quantity: this.quantityExtras$.value
      });
    }
    this.cdr.markForCheck();
  }

  public onRemovePromo = (): void => {
    this.quantity = 0;
    this.quantityPromoChange.emit({ ClavePromo: this.promoData.ClavePromo, quantity: 0 });
    this.extrasQuantity();
    if (this.emitirExtras) {
      this.quantityPromoExtrasChange.emit({
        ClavePromo: this.promoData.ClavePromo,
        clave_producto: this.claveProducto,
        quantity: 0
      });
    }
    this.cdr.markForCheck();
  }

  private callBackStartAutoPlay = (slideIndex: number) => {
    this.activeSlide = slideIndex;
    this.cdr.markForCheck();
  };

  private extrasQuantity = () => {
    this.emitirExtras = false;
    const isEstaticoExtras = this.promoData.TipoPromocion === 'ESTATICO' && this.promoData.Productos?.length === 1;
    if (isEstaticoExtras && this.quantity > this.promoData.MaximoPaquetes) {
      this.quantityExtras$.next(this.quantity - this.promoData.MaximoPaquetes);
      this.emitirExtras = true;
    } else if (isEstaticoExtras && this.quantity <= this.promoData.MaximoPaquetes && this.quantityExtras$.value > 0) {
      this.quantityExtras$.next(0);
      this.emitirExtras = true;
    }
  }

}
